import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu akcji i banerów partnerskich.
 * Weryfikuje po stronie serwera API, czy aktualnie zalogowany użytkownik posiada
 * uprawnienia do przeglądania statystyk i zarządzania kampaniami marketingowymi.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerBannerActionAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu modułu marketingowego.
   * Zabezpiecza przed nieautoryzowanym transferem logiki biznesowej kampanii partnerskich
   * do przeglądarki klienta, wymuszając walidację po stronie serwera.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/partner-banner-actions-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia przed aktywacją i wyrenderowaniem widoku panelu kampanii.
   * Obsługuje błędy wygaśnięcia sesji (HTTP 401) wymuszając proces wylogowania,
   * a w przypadku braku wystarczających ról systemowych,
   * wykorzystuje klasę CommonUtils do zaprezentowania komunikatu błędu.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partner-banner-actions-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
